import type {
    QuestionnaireAnswerInput,
    QuestionnaireQuestionChoiceFragment,
    QuestionnaireQuestionChoiceLayoutInput,
} from '@/graphql/api'
import { assertNever } from '@/helpers'

export interface AnswerChoice {
    addAnswer: (optionId: string) => void
    removeAnswer: (optionId: string) => void
}

export function useAnswerChoice(
    props: {
        question: QuestionnaireQuestionChoiceFragment
        answer?: QuestionnaireAnswerInput
    },
    emit: (event: 'update:answer', payload?: QuestionnaireAnswerInput) => void
): AnswerChoice {
    const addAnswer = (optionId: string) => {
        const questionOption = props.question.options.find(
            (option) => option.id === optionId
        )
        if (!questionOption) {
            return
        }

        const layout: QuestionnaireQuestionChoiceLayoutInput = {}

        switch (questionOption.content.__typename) {
            case 'QuestionnaireQuestionChoiceOptionContentText':
                layout.text = {
                    title: questionOption.content?.title,
                    description: questionOption.content?.description,
                }
                break
            case 'QuestionnaireQuestionChoiceOptionContentImage':
                layout.image = {
                    image: questionOption.content?.image?.url,
                }
                break
            case 'QuestionnaireQuestionChoiceOptionContentImageCard':
                layout.imageCard = {
                    title: questionOption.content?.title,
                    description: questionOption.content?.description,
                    image: questionOption.content?.image?.url,
                }
                break
            case 'QuestionnaireQuestionChoiceOptionContentIconCard':
                layout.iconCard = {
                    backgroundColor: questionOption.content.backgroundColor,
                    title: questionOption.content.title,
                    description: questionOption?.content.description,
                    image: questionOption?.content.icon.url,
                }
                break
            default:
                assertNever(questionOption?.content, undefined)
        }

        const answer: QuestionnaireAnswerInput = {
            question: {
                referenceId: props.question.id,
                title: props.question.title,
            },
            answer: props.question.multiple
                ? {
                      multiChoice: [
                          ...(props.answer?.answer.multiChoice || []),
                          {
                              referenceId: optionId,
                              layout: layout,
                          },
                      ],
                  }
                : {
                      singleChoice: {
                          referenceId: optionId,
                          layout: layout,
                      },
                  },
        }

        emit('update:answer', answer)
    }

    const removeAnswer = (optionId: string) => {
        const answer: QuestionnaireAnswerInput | undefined = props.question
            .multiple
            ? {
                  ...props.answer,
                  question: {
                      referenceId: props.question.id,
                      title: props.question.title,
                  },
                  answer: {
                      multiChoice: props.answer?.answer.multiChoice?.filter(
                          (o) => o.referenceId !== optionId
                      ),
                  },
              }
            : undefined

        emit('update:answer', answer)
    }

    return {
        addAnswer,
        removeAnswer,
    }
}
